/**
 * Copy a value and say whether it worked.
 *
 * Four menus had their own copy of this - a `try`, a `writeText`, and two toast
 * keys - and the reason it has to be said at all is that `navigator.clipboard`
 * is permission-gated and simply absent over plain http. A copy entry that
 * silently did nothing on the office machine's http staging build looked like a
 * broken menu rather than a browser rule.
 */
import { useCallback } from "react";
import { useT } from "../../i18n/I18nProvider";
import { useToast } from "./Toast";

export function useCopyToast(): (value: string) => void {
  const t = useT();
  const toast = useToast();

  return useCallback(
    (value: string) => {
      // Optional chaining, not a try: over http `navigator.clipboard` is not a
      // failing API, it is missing entirely, and reading `.writeText` off it
      // throws before any promise exists to catch.
      const written = navigator.clipboard?.writeText(value);
      if (!written) {
        toast(t("common.copyFailed"));
        return;
      }
      void written.then(() => toast(t("common.copied"))).catch(() => toast(t("common.copyFailed")));
    },
    [t, toast],
  );
}
